import React, { useEffect, useRef, useState } from "react";
import { Popover } from "antd";
import { Building2, Check, ChevronsUpDown, Search } from "lucide-react";

import {
	ClaimableJurisdiction,
	filterJurisdictionsBySearch,
	formatJurisdictionRegion
} from "utils/claim-jurisdiction";

type ClaimJurisdictionPickerProps = {
	jurisdictions: ClaimableJurisdiction[];
	selectedJurisdictionId: string | null;
	onSelect: (jurisdictionId: string) => void;
	// id of the label above the picker, for screen readers.
	labelId: string;
};

/**
 * Button that opens a searchable list of jurisdictions. The list filters by name and place
 * while the person types, and it works with the arrow keys and Enter.
 */
const ClaimJurisdictionPicker = ({
	jurisdictions,
	selectedJurisdictionId,
	onSelect,
	labelId
}: ClaimJurisdictionPickerProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [searchText, setSearchText] = useState("");
	const [highlightedIndex, setHighlightedIndex] = useState(0);
	const listRef = useRef<HTMLDivElement>(null);
	const searchInputRef = useRef<HTMLInputElement>(null);

	const selectedJurisdiction = jurisdictions.find(jurisdiction => jurisdiction.id === selectedJurisdictionId);
	const visibleJurisdictions = filterJurisdictionsBySearch(jurisdictions, searchText);

	// Opening the list starts from the selected jurisdiction, or from the first one.
	useEffect(() => {
		if (!isOpen) return;

		setSearchText("");
		const selectedIndex = jurisdictions.findIndex(jurisdiction => jurisdiction.id === selectedJurisdictionId);
		setHighlightedIndex(Math.max(selectedIndex, 0));

		// The list renders in a portal after the click, so autoFocus does not reach the search field.
		const focusTimeout = window.setTimeout(() => searchInputRef.current?.focus(), 0);
		return () => window.clearTimeout(focusTimeout);
	}, [isOpen]);

	// Keeps the highlighted row visible while the arrow keys move it.
	useEffect(() => {
		const highlightedRow = listRef.current?.querySelector(`[data-index="${highlightedIndex}"]`);
		highlightedRow?.scrollIntoView({ block: "nearest" });
	}, [highlightedIndex]);

	const selectJurisdiction = (jurisdictionId: string) => {
		onSelect(jurisdictionId);
		setIsOpen(false);
	};

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchText(event.target.value);
		setHighlightedIndex(0);
	};

	const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		const lastIndex = visibleJurisdictions.length - 1;

		if (event.key === "ArrowDown") {
			event.preventDefault();
			setHighlightedIndex(Math.min(highlightedIndex + 1, lastIndex));
		} else if (event.key === "ArrowUp") {
			event.preventDefault();
			setHighlightedIndex(Math.max(highlightedIndex - 1, 0));
		} else if (event.key === "Enter") {
			event.preventDefault();
			const highlightedJurisdiction = visibleJurisdictions[highlightedIndex];
			if (highlightedJurisdiction) selectJurisdiction(highlightedJurisdiction.id);
		} else if (event.key === "Escape") {
			setIsOpen(false);
		}
	};

	const jurisdictionRows = visibleJurisdictions.map((jurisdiction, index) => {
		const isHighlighted = index === highlightedIndex;
		const isSelected = jurisdiction.id === selectedJurisdictionId;

		return (
			<div
				key={jurisdiction.id}
				data-index={index}
				role="option"
				aria-selected={isSelected}
				className={`claim-jurisdiction-picker-option ${isHighlighted ? "claim-jurisdiction-picker-option-highlighted" : ""}`}
				onMouseMove={() => setHighlightedIndex(index)}
				onClick={() => selectJurisdiction(jurisdiction.id)}
			>
				<div className="claim-jurisdiction-picker-option-text">
					<div>{jurisdiction.name}</div>
					<div className="claim-jurisdiction-picker-option-region">{formatJurisdictionRegion(jurisdiction)}</div>
				</div>
				{isSelected && <Check size={16} className="claim-jurisdiction-picker-option-check" />}
			</div>
		);
	});

	const panel = (
		<div className="claim-jurisdiction-picker-panel">
			<div className="claim-jurisdiction-picker-search">
				<Search size={16} className="claim-jurisdiction-picker-search-icon" />
				<input
					ref={searchInputRef}
					value={searchText}
					onChange={handleSearchChange}
					onKeyDown={handleSearchKeyDown}
					placeholder="Search name or city…"
					aria-label="Search jurisdictions"
					className="claim-jurisdiction-picker-search-input"
				/>
			</div>
			<div ref={listRef} role="listbox" className="claim-jurisdiction-picker-list">
				{visibleJurisdictions.length === 0 ? (
					<div className="claim-jurisdiction-picker-empty">No matching jurisdiction. Try a nearby city.</div>
				) : (
					jurisdictionRows
				)}
			</div>
		</div>
	);

	return (
		<Popover
			open={isOpen}
			onOpenChange={setIsOpen}
			trigger="click"
			placement="bottomLeft"
			arrow={false}
			align={{ offset: [0, 4] }}
			transitionName=""
			destroyTooltipOnHide
			rootClassName="claim-jurisdiction-picker-popover"
			content={panel}
		>
			<button
				type="button"
				role="combobox"
				aria-expanded={isOpen}
				aria-labelledby={labelId}
				className="claim-jurisdiction-picker-trigger"
			>
				<Building2 size={20} className="claim-jurisdiction-picker-trigger-icon" />
				<span className="claim-jurisdiction-picker-trigger-text">
					{selectedJurisdiction?.name ?? "Search for your jurisdiction"}
				</span>
				<ChevronsUpDown size={16} className="claim-jurisdiction-picker-trigger-chevron" />
			</button>
		</Popover>
	);
};

export default ClaimJurisdictionPicker;
